/**
 * Care-flow component store (Sprint 4.3.4, PROD-653).
 *
 * Holds the in-memory 16-folder care-flow component bundle for the
 * editor. The bundle is derived on-demand from the active pathway via
 * `pathwayToCareFlowComponents` (the serializer is the source of
 * truth); user authoring lands as drafts here, and the publish workflow
 * commits drafts back into the canonical bundle.
 *
 * HC5: zero em / en dashes.
 */
import { create } from 'zustand';
import {
  CARE_FLOW_FOLDERS,
  emptyCareFlowBundle,
  type CareFlowComponentBundle,
  type CareFlowFolder,
} from '../services/care-flow-components/types';
import {
  pathwayToCareFlowComponents,
} from '../services/care-flow-components/serializer';
import type { EditablePathway } from './pathway-types';

export interface ComponentDraft {
  folder: CareFlowFolder;
  componentId: string;
  candidate: Record<string, unknown>;
}

interface BundleByKey {
  bundle: CareFlowComponentBundle;
  drafts: Record<string, ComponentDraft>;
}

export interface CareFlowComponentStoreState {
  byPathwayKey: Record<string, BundleByKey>;
  hydrateFromPathway: (
    pathwayKey: string,
    pathway: EditablePathway,
  ) => CareFlowComponentBundle;
  getBundle: (
    pathwayKey: string,
    defaultPathway?: EditablePathway,
  ) => CareFlowComponentBundle;
  setDraft: (pathwayKey: string, draft: ComponentDraft) => void;
  discardDraft: (
    pathwayKey: string,
    folder: CareFlowFolder,
    componentId: string,
  ) => void;
  commitDraft: (
    pathwayKey: string,
    folder: CareFlowFolder,
    componentId: string,
  ) => void;
  clearAllDrafts: (pathwayKey: string) => void;
  hasOpenDrafts: (pathwayKey: string) => boolean;
}

const draftKey = (folder: CareFlowFolder, componentId: string) =>
  `${folder}::${componentId}`;

export const useCareFlowComponentStore = create<CareFlowComponentStoreState>(
  (set, get) => ({
    byPathwayKey: {},
    hydrateFromPathway: (pathwayKey, pathway) => {
      const bundle = pathwayToCareFlowComponents(pathway);
      set({
        byPathwayKey: {
          ...get().byPathwayKey,
          [pathwayKey]: { bundle, drafts: {} },
        },
      });
      return bundle;
    },
    getBundle: (pathwayKey, defaultPathway) => {
      const cached = get().byPathwayKey[pathwayKey];
      if (cached) return cached.bundle;
      if (defaultPathway) {
        return get().hydrateFromPathway(pathwayKey, defaultPathway);
      }
      return emptyCareFlowBundle();
    },
    setDraft: (pathwayKey, draft) => {
      const current = get().byPathwayKey[pathwayKey];
      const baseBundle = current?.bundle ?? emptyCareFlowBundle();
      const baseDrafts = current?.drafts ?? {};
      set({
        byPathwayKey: {
          ...get().byPathwayKey,
          [pathwayKey]: {
            bundle: baseBundle,
            drafts: {
              ...baseDrafts,
              [draftKey(draft.folder, draft.componentId)]: draft,
            },
          },
        },
      });
    },
    discardDraft: (pathwayKey, folder, componentId) => {
      const current = get().byPathwayKey[pathwayKey];
      if (!current) return;
      const next = { ...current.drafts };
      delete next[draftKey(folder, componentId)];
      set({
        byPathwayKey: {
          ...get().byPathwayKey,
          [pathwayKey]: { bundle: current.bundle, drafts: next },
        },
      });
    },
    commitDraft: (pathwayKey, folder, componentId) => {
      const current = get().byPathwayKey[pathwayKey];
      if (!current) return;
      const draft = current.drafts[draftKey(folder, componentId)];
      if (!draft) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const folderArr = current.bundle[folder] as any[];
      const idx = folderArr.findIndex(
        (c) => (c as { id?: string }).id === componentId,
      );
      let nextArr: unknown[];
      if (idx >= 0) {
        nextArr = [...folderArr];
        nextArr[idx] = draft.candidate;
      } else {
        nextArr = [...folderArr, draft.candidate];
      }
      const nextBundle = {
        ...current.bundle,
        [folder]: nextArr,
      } as CareFlowComponentBundle;
      const nextDrafts = { ...current.drafts };
      delete nextDrafts[draftKey(folder, componentId)];
      set({
        byPathwayKey: {
          ...get().byPathwayKey,
          [pathwayKey]: { bundle: nextBundle, drafts: nextDrafts },
        },
      });
    },
    clearAllDrafts: (pathwayKey) => {
      const current = get().byPathwayKey[pathwayKey];
      if (!current) return;
      set({
        byPathwayKey: {
          ...get().byPathwayKey,
          [pathwayKey]: { bundle: current.bundle, drafts: {} },
        },
      });
    },
    hasOpenDrafts: (pathwayKey) => {
      const current = get().byPathwayKey[pathwayKey];
      if (!current) return false;
      return Object.keys(current.drafts).length > 0;
    },
  }),
);

export { CARE_FLOW_FOLDERS };
